import { env } from '@/env';
import { getAccessToken } from '@/lib/supabase/auth';

/** The error envelope every non-2xx response carries. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    requestId: string;
    retryable: boolean;
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> | undefined,
    readonly requestId: string | undefined,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Distinguishes "not an employee yet" from "wrong domain" for the rejection screens. */
  get reason(): string | undefined {
    const reason = this.details?.['reason'];
    return typeof reason === 'string' ? reason : undefined;
  }
}

export interface ApiRequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  readonly idempotencyKey?: string;
}

/**
 * Builds a query string, skipping anything absent.
 *
 * `undefined` and `null` are DROPPED rather than serialised — `?cursor=undefined` is a
 * string the server has to reject, and it is the kind of bug that only shows up on the
 * second page. An array appends the key once per value, which is how the API reads
 * repeated filters.
 */
export function withQuery(
  path: string,
  params: Record<string, string | number | boolean | readonly string[] | null | undefined>,
): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) value.forEach((item) => search.append(key, item));
    else search.set(key, String(value));
  }

  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const token = await getAccessToken();

  const headers: Record<string, string> = { accept: 'application/json' };
  if (token) headers['authorization'] = `Bearer ${token}`;
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  // `path` is relative to the versioned base — env.apiUrl already ends in /api/v1, so
  // nothing here repeats it and moving to a new version is a deploy variable.
  const response = await fetch(`${env.apiUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'unknown_error',
      body?.error?.message ?? `Request failed with ${response.status}.`,
      body?.error?.details,
      body?.error?.requestId ?? response.headers.get('x-request-id') ?? undefined,
      body?.error?.retryable ?? false,
    );
  }

  return payload as T;
}
